import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ImageUpload from "../ImageUpload";
import { Save, Plus, X, ChevronDown, ChevronUp, Edit2, Trash2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fetchAdmin(url, opts = {}) {
  return fetch(`${API_URL}${url}`, { credentials: "include", ...opts });
}

export default function AdventureBuilderPanel() {
  const [templates, setTemplates] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const [tRes, qRes] = await Promise.all([
        fetchAdmin("/api/content/admin/adventure-templates"),
        fetchAdmin("/api/content/admin/profile-questions"),
      ]);
      if (tRes.ok) setTemplates(await tRes.json());
      if (qRes.ok) setQuestions(await qRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function saveTemplate() {
    if (!editingTemplate) return;
    setSaving(true);
    await fetchAdmin(`/api/content/admin/adventure-templates/${editingTemplate.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editingTemplate),
    });
    setSaving(false);
    setEditingTemplate(null);
    loadAll();
  }

  async function saveQuestions() {
    setSaving(true);
    await fetchAdmin("/api/content/admin/profile-questions", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ questions }),
    });
    setSaving(false);
    setEditingQuestions(false);
    loadAll();
  }

  function updateDecisionOption(dpIdx, optIdx, field, value) {
    setEditingTemplate(prev => {
      const dp = [...prev.decisionPoints];
      const opts = [...dp[dpIdx].options];
      opts[optIdx] = { ...opts[optIdx], [field]: value };
      dp[dpIdx] = { ...dp[dpIdx], options: opts };
      return { ...prev, decisionPoints: dp };
    });
  }

  function updateDecisionPoint(dpIdx, field, value) {
    setEditingTemplate(prev => {
      const dp = [...prev.decisionPoints];
      dp[dpIdx] = { ...dp[dpIdx], [field]: value };
      return { ...prev, decisionPoints: dp };
    });
  }

  function addDecisionOption(dpIdx) {
    setEditingTemplate(prev => {
      const dp = [...prev.decisionPoints];
      dp[dpIdx] = {
        ...dp[dpIdx],
        options: [...dp[dpIdx].options, { id: `opt-${Date.now()}`, title: "", description: "", impact: { days: 0, cost: 0, difficulty: 0 }, badge: "" }],
      };
      return { ...prev, decisionPoints: dp };
    });
  }

  function removeDecisionOption(dpIdx, optIdx) {
    setEditingTemplate(prev => {
      const dp = [...prev.decisionPoints];
      dp[dpIdx] = { ...dp[dpIdx], options: dp[dpIdx].options.filter((_, i) => i !== optIdx) };
      return { ...prev, decisionPoints: dp };
    });
  }

  function addDecisionPoint() {
    setEditingTemplate(prev => ({
      ...prev,
      decisionPoints: [...prev.decisionPoints, { id: `dp-${Date.now()}`, title: "", description: "", timing: "", options: [] }],
    }));
  }

  function removeDecisionPoint(dpIdx) {
    setEditingTemplate(prev => ({
      ...prev,
      decisionPoints: prev.decisionPoints.filter((_, i) => i !== dpIdx),
    }));
  }

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" /></div>;

  return (
    <div className="space-y-6" data-testid="adventure-builder-panel">
      {/* Adventure Templates */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Adventure Templates ({templates.length})</h2>
        <p className="text-stone-400 text-sm mb-4">These templates define the "Choose Your Adventure" builder flow — each template is a trek with decision points that users can customize.</p>

        {editingTemplate ? (
          <div className="bg-stone-900/60 border border-stone-800 rounded-xl p-6 space-y-4" data-testid="template-edit-form">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold">Editing: {editingTemplate.name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(null)} className="text-stone-400"><X className="w-4 h-4" /></Button>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-stone-300">Name</Label><Input value={editingTemplate.name} onChange={e => setEditingTemplate(p => ({ ...p, name: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
              <div><Label className="text-stone-300">Short Name</Label><Input value={editingTemplate.shortName} onChange={e => setEditingTemplate(p => ({ ...p, shortName: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
              <div><Label className="text-stone-300">Region</Label><Input value={editingTemplate.region} onChange={e => setEditingTemplate(p => ({ ...p, region: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
              <div><Label className="text-stone-300">Base Price (USD)</Label><Input type="number" value={editingTemplate.basePrice} onChange={e => setEditingTemplate(p => ({ ...p, basePrice: parseInt(e.target.value) || 0 }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
              <div><Label className="text-stone-300">Base Duration (days)</Label><Input type="number" value={editingTemplate.baseDuration} onChange={e => setEditingTemplate(p => ({ ...p, baseDuration: parseInt(e.target.value) || 1 }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
              <div><Label className="text-stone-300">Base Difficulty</Label><Input value={editingTemplate.baseDifficulty} onChange={e => setEditingTemplate(p => ({ ...p, baseDifficulty: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" /></div>
            </div>
            <div><Label className="text-stone-300">Description</Label><Textarea value={editingTemplate.description} onChange={e => setEditingTemplate(p => ({ ...p, description: e.target.value }))} className="mt-1 bg-stone-800 border-stone-700 text-white rounded-xl" rows={2} /></div>
            <ImageUpload value={editingTemplate.heroImage} onChange={v => setEditingTemplate(p => ({ ...p, heroImage: v }))} label="Hero Image" />

            {/* Decision Points */}
            <div className="border-t border-stone-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-semibold">Decision Points ({editingTemplate.decisionPoints?.length || 0})</h4>
                <Button type="button" variant="outline" size="sm" onClick={addDecisionPoint} className="border-stone-700 text-stone-300 rounded-lg"><Plus className="w-3 h-3 mr-1" />Add Decision</Button>
              </div>

              {editingTemplate.decisionPoints?.map((dp, dpIdx) => (
                <div key={dpIdx} className="bg-stone-800/60 border border-stone-700 rounded-lg p-4 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-amber-400 text-sm font-semibold">Decision {dpIdx + 1}</span>
                    <button type="button" onClick={() => removeDecisionPoint(dpIdx)} className="text-stone-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Input placeholder="Title" value={dp.title} onChange={e => updateDecisionPoint(dpIdx, "title", e.target.value)} className="bg-stone-700 border-stone-600 text-white text-sm rounded-lg" />
                    <Input placeholder="Timing (e.g. day3)" value={dp.timing} onChange={e => updateDecisionPoint(dpIdx, "timing", e.target.value)} className="bg-stone-700 border-stone-600 text-white text-sm rounded-lg" />
                  </div>
                  <Input placeholder="Description" value={dp.description} onChange={e => updateDecisionPoint(dpIdx, "description", e.target.value)} className="bg-stone-700 border-stone-600 text-white text-sm rounded-lg mb-3" />

                  {/* Options */}
                  <div className="space-y-2">
                    <p className="text-stone-400 text-xs font-medium">Options:</p>
                    {dp.options?.map((opt, optIdx) => (
                      <div key={optIdx} className="bg-stone-700/50 rounded-lg p-3 relative">
                        <button type="button" onClick={() => removeDecisionOption(dpIdx, optIdx)} className="absolute top-2 right-2 text-stone-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <Input placeholder="Title" value={opt.title} onChange={e => updateDecisionOption(dpIdx, optIdx, "title", e.target.value)} className="bg-stone-600 border-stone-500 text-white text-xs rounded-lg" />
                          <Input placeholder="Badge" value={opt.badge} onChange={e => updateDecisionOption(dpIdx, optIdx, "badge", e.target.value)} className="bg-stone-600 border-stone-500 text-white text-xs rounded-lg" />
                        </div>
                        <Input placeholder="Description" value={opt.description} onChange={e => updateDecisionOption(dpIdx, optIdx, "description", e.target.value)} className="bg-stone-600 border-stone-500 text-white text-xs rounded-lg mb-2" />
                        <div className="flex gap-2">
                          <div className="flex items-center gap-1"><span className="text-stone-400 text-xs">Days:</span><Input type="number" value={opt.impact?.days || 0} onChange={e => updateDecisionOption(dpIdx, optIdx, "impact", { ...opt.impact, days: parseInt(e.target.value) || 0 })} className="bg-stone-600 border-stone-500 text-white text-xs rounded-lg w-16 h-7" /></div>
                          <div className="flex items-center gap-1"><span className="text-stone-400 text-xs">Cost:</span><Input type="number" value={opt.impact?.cost || 0} onChange={e => updateDecisionOption(dpIdx, optIdx, "impact", { ...opt.impact, cost: parseInt(e.target.value) || 0 })} className="bg-stone-600 border-stone-500 text-white text-xs rounded-lg w-20 h-7" /></div>
                          <div className="flex items-center gap-1"><span className="text-stone-400 text-xs">Diff:</span><Input type="number" value={opt.impact?.difficulty || 0} onChange={e => updateDecisionOption(dpIdx, optIdx, "impact", { ...opt.impact, difficulty: parseInt(e.target.value) || 0 })} className="bg-stone-600 border-stone-500 text-white text-xs rounded-lg w-16 h-7" /></div>
                        </div>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => addDecisionOption(dpIdx)} className="border-stone-600 text-stone-400 rounded-lg text-xs h-7"><Plus className="w-3 h-3 mr-1" />Option</Button>
                  </div>
                </div>
              ))}
            </div>

            <Button onClick={saveTemplate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-template-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving..." : "Save Template"}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map(t => (
              <div key={t.id} className="bg-stone-900/60 border border-stone-800 rounded-xl p-5 flex items-center justify-between" data-testid={`adventure-template-${t.id}`}>
                <div className="flex items-center gap-4">
                  {t.heroImage && <img src={t.heroImage} alt="" className="w-16 h-12 rounded-lg object-cover hidden sm:block" />}
                  <div>
                    <h3 className="text-white font-semibold">{t.name}</h3>
                    <p className="text-stone-400 text-sm">{t.region} | {t.baseDuration} days | USD ${t.basePrice} | {t.decisionPoints?.length || 0} decision points</p>
                  </div>
                </div>
                <Button onClick={() => setEditingTemplate({ ...t })} variant="ghost" size="sm" className="text-stone-400 hover:text-amber-400"><Edit2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile Questions */}
      <div className="border-t border-stone-800 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Profile Questions ({questions.length})</h2>
            <p className="text-stone-400 text-sm">Questions asked during the adventure builder to profile the trekker</p>
          </div>
          <Button onClick={() => setEditingQuestions(!editingQuestions)} variant="outline" className="border-stone-700 text-stone-300 rounded-xl">
            {editingQuestions ? "Cancel" : <><Edit2 className="w-4 h-4 mr-1.5" />Edit Questions</>}
          </Button>
        </div>

        {editingQuestions ? (
          <div className="space-y-3">
            {questions.map((q, qi) => (
              <div key={qi} className="bg-stone-900/60 border border-stone-800 rounded-xl p-4 relative">
                <button type="button" onClick={() => setQuestions(questions.filter((_, i) => i !== qi))} className="absolute top-3 right-3 text-stone-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Input placeholder="Question" value={q.question} onChange={e => { const u = [...questions]; u[qi] = { ...u[qi], question: e.target.value }; setQuestions(u); }} className="bg-stone-800 border-stone-700 text-white text-sm rounded-lg" />
                  <div className="flex gap-2">
                    <select value={q.type} onChange={e => { const u = [...questions]; u[qi] = { ...u[qi], type: e.target.value }; setQuestions(u); }} className="bg-stone-800 border border-stone-700 text-white text-sm rounded-lg h-10 px-2 flex-1">
                      <option value="single">Single Choice</option><option value="multiple">Multiple Choice</option><option value="text">Text</option>
                    </select>
                    <Input placeholder="Timing" value={q.timing} onChange={e => { const u = [...questions]; u[qi] = { ...u[qi], timing: e.target.value }; setQuestions(u); }} className="bg-stone-800 border-stone-700 text-white text-sm rounded-lg flex-1" />
                  </div>
                </div>
                {q.type !== "text" && (
                  <div className="space-y-1 mt-2">
                    <p className="text-stone-500 text-xs">Options:</p>
                    {q.options?.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <Input placeholder="Label" value={opt.label} onChange={e => { const u = [...questions]; const o = [...u[qi].options]; o[oi] = { ...o[oi], label: e.target.value }; u[qi] = { ...u[qi], options: o }; setQuestions(u); }} className="bg-stone-700 border-stone-600 text-white text-xs rounded-lg flex-1" />
                        <Input placeholder="Value" value={opt.value} onChange={e => { const u = [...questions]; const o = [...u[qi].options]; o[oi] = { ...o[oi], value: e.target.value }; u[qi] = { ...u[qi], options: o }; setQuestions(u); }} className="bg-stone-700 border-stone-600 text-white text-xs rounded-lg w-24" />
                        <button type="button" onClick={() => { const u = [...questions]; u[qi] = { ...u[qi], options: u[qi].options.filter((_, i) => i !== oi) }; setQuestions(u); }} className="text-stone-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => { const u = [...questions]; u[qi] = { ...u[qi], options: [...(u[qi].options || []), { label: "", value: "" }] }; setQuestions(u); }} className="border-stone-700 text-stone-400 rounded-lg text-xs h-7"><Plus className="w-3 h-3 mr-1" />Option</Button>
                  </div>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" className="border-stone-700 text-stone-300 rounded-lg" onClick={() => setQuestions([...questions, { id: `q-${Date.now()}`, question: "", type: "single", timing: "", options: [] }])}><Plus className="w-3 h-3 mr-1" />Add Question</Button>
            <div><Button onClick={saveQuestions} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-questions-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? "Saving..." : "Save Questions"}</Button></div>
          </div>
        ) : (
          <div className="space-y-2">
            {questions.map((q, i) => (
              <div key={i} className="bg-stone-900/60 border border-stone-800 rounded-xl p-4">
                <p className="text-white text-sm font-medium">{i + 1}. {q.question}</p>
                <p className="text-stone-500 text-xs mt-1">Type: {q.type} | Timing: {q.timing} | {q.options?.length || 0} options</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
